/**
 * Calendario de visitas y asignación de personal.
 *
 * Una visita no realizada exige motivo: sin eso, un hueco en el calendario es
 * indistinguible de un olvido, y el consultorio pregunta.
 *
 * La cuadrícula semanal (Lun-Dom · 5 a.m.-5 p.m.) está siempre visible, haya
 * o no visitas: un calendario que desaparece cuando está vacío no sirve para
 * programar la primera visita.
 *
 * ── Dos capas de visita (0024) ──────────────────────────────────────────────
 * La **base** es la ronda garantizada: una por consultorio y por día. La
 * **extra** es la que pide el cliente, y solo entra en la capacidad que sobra.
 * El chip lo dice con una etiqueta y no con un color, porque el color ya está
 * ocupado por el estado y un solo canal no puede llevar dos mensajes.
 *
 * ── El horario 05:00-17:00 no se amplía ─────────────────────────────────────
 * Es el horario de servicio del negocio, no un límite de esta pantalla. Lo que
 * cambió es qué pasa con lo que cae afuera: antes, `slotHour()` acotaba la hora
 * al rango y una visita de las 18:00 se dibujaba en la franja de las 16:00 —el
 * calendario mentía en silencio—. Ahora va a su propia fila, marcada y con un
 * aviso arriba.
 */

import { Fragment, useState } from 'react';
import {
  addDays, dateLabel, DEFAULT_CLEANING_DEADLINE, isWithinServiceWindow, SERVICE_WINDOW_END,
  SERVICE_WINDOW_START, startOfWeek, today, VISIT_KIND_HINT, VISIT_KIND_LABEL,
  VISIT_STATUS_LABEL, VISIT_STATUS_TONE,
  type IsoDate, type VisitKind, type VisitStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Button, Field, Modal, Note, PageHead, StatusBadge,
} from '@/components/ui';
import { slotOf } from './calendar-slots';
import '@/styles/agenda.css';

interface Visit {
  id: string; unit_code: string; unit_name: string | null; scheduled_date: string;
  scheduled_start: string | null; scheduled_end: string | null;
  status: VisitStatus; visit_kind: VisitKind; assigned_to: string | null; skip_reason: string | null;
}

const WEEKDAY_LABEL = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
/* El rango sale del dominio, no de dos números sueltos acá: es el horario de
   servicio del negocio y lo comparten el Worker y esta pantalla. */
const START_HOUR = Number(SERVICE_WINDOW_START.slice(0, 2));
const END_HOUR = Number(SERVICE_WINDOW_END.slice(0, 2)); // límite exclusivo de la última franja

function hourLabel(h: number): string {
  const period = h < 12 ? 'a.m.' : 'p.m.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}


export function CalendarPage() {
  const [weekStart, setWeekStart] = useState<IsoDate>(startOfWeek(today()));
  const weekEnd = addDays(weekStart, 6);
  const query = useApi<{ visits: Visit[] }>(`/admin/visits?from=${weekStart}&to=${weekEnd}`);
  const [creating, setCreating] = useState<{ date: IsoDate; time?: string } | null>(null);
  const [viewing, setViewing] = useState<Visit | null>(null);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

  return (
    <>
      <PageHead
        title="Calendario"
        subtitle="Una semana de visitas programadas y quién las atiende."
        actions={<Button variant="primary" onClick={() => setCreating({ date: today() })}>Programar visita</Button>}
      />

      <div className="row row--wrap mb-2" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="row" style={{ gap: 6 }}>
          <Button onClick={() => setWeekStart((w) => addDays(w, -7))} aria-label="Semana anterior">←</Button>
          <Button onClick={() => setWeekStart(startOfWeek(today()))}>Hoy</Button>
          <Button onClick={() => setWeekStart((w) => addDays(w, 7))} aria-label="Semana siguiente">→</Button>
        </div>
        <strong className="small">{dateLabel(weekStart)} – {dateLabel(weekEnd)}</strong>
      </div>

      <Async query={query}>
        {(data) => {
          const byDayHour = new Map<string, Visit[]>();
          const unscheduled = new Map<string, Visit[]>();
          /* Las que caen fuera de 05:00-17:00. Antes iban a parar a la franja
             más cercana del rango y ahí se perdían: nadie podía notar que la
             hora dibujada no era la hora guardada. */
          const outside = new Map<string, Visit[]>();

          for (const v of data.visits) {
            const slot = slotOf(v);
            const bucket = slot.kind === 'hour' ? byDayHour
              : (slot.kind === 'outside' ? outside : unscheduled);
            const key = slot.kind === 'hour' ? `${v.scheduled_date}T${slot.hour}` : v.scheduled_date;
            (bucket.get(key) ?? bucket.set(key, []).get(key)!).push(v);
          }
          const hasUnscheduled = unscheduled.size > 0;
          const hasOutside = outside.size > 0;
          const outsideCount = [...outside.values()].reduce((a, list) => a + list.length, 0);
          const extraRows = (hasUnscheduled ? 1 : 0) + (hasOutside ? 1 : 0);

          return (
            <div>
              {hasOutside && (
                <div className="cal-outside-note" role="status">
                  <span aria-hidden="true">⚠</span>
                  <span>
                    <strong>
                      {outsideCount === 1
                        ? 'Hay una visita fuera del horario de servicio'
                        : `Hay ${outsideCount} visitas fuera del horario de servicio`}
                    </strong>{' '}
                    ({SERVICE_WINDOW_START} a {SERVICE_WINDOW_END}). Se muestran en su propia fila,
                    con la hora real: la cuadrícula no las reubica.
                  </span>
                </div>
              )}

              <div className="cal-grid__scroll">
                <div
                  className="cal-grid__inner"
                  style={{ gridTemplateRows: `auto ${'auto '.repeat(extraRows)}repeat(${hours.length}, 56px)` }}
                >
                  <div className="cal-head cal-head--corner" />
                  {days.map((d) => (
                    <div key={d} className={`cal-head${d === today() ? ' cal-head--today' : ''}`}>
                      <span className="cal-head__dow">{WEEKDAY_LABEL[(new Date(`${d}T12:00:00`).getDay() + 6) % 7]}</span>
                      <span className="cal-head__date">{dateLabel(d).slice(0, 5)}</span>
                    </div>
                  ))}

                  {hasOutside && (
                    <>
                      <div className="cal-time cal-time--alert">Fuera de horario</div>
                      {days.map((d) => (
                        <div key={d} className="cal-cell">
                          {(outside.get(d) ?? []).map((v) => (
                            <VisitChip key={v.id} visit={v} outside onClick={() => setViewing(v)} />
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {hasUnscheduled && (
                    <>
                      <div className="cal-time cal-time--flex">Sin hora</div>
                      {days.map((d) => (
                        <div key={d} className="cal-cell">
                          {(unscheduled.get(d) ?? []).map((v) => (
                            <VisitChip key={v.id} visit={v} onClick={() => setViewing(v)} />
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {hours.map((h) => (
                    <Fragment key={h}>
                      <div className="cal-time">{hourLabel(h)}</div>
                      {days.map((d) => {
                        const key = `${d}T${h}`;
                        const cellVisits = byDayHour.get(key) ?? [];
                        return (
                          <div
                            key={key}
                            className="cal-cell cal-cell--slot"
                            role="button"
                            tabIndex={0}
                            onClick={() => setCreating({ date: d, time: `${pad2(h)}:00` })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') setCreating({ date: d, time: `${pad2(h)}:00` });
                            }}
                          >
                            {cellVisits.map((v) => (
                              <VisitChip
                                key={v.id}
                                visit={v}
                                onClick={(e) => { e.stopPropagation(); setViewing(v); }}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </Fragment>
                  ))}
                </div>
              </div>

              <div className="cal-legend">
                <span className="cal-legend__item">
                  <span className="cal-legend__chip">Extra</span>
                  A pedido del cliente. Entra en la capacidad que sobra; la ronda base va sin marca.
                </span>
                <span className="cal-legend__item">
                  <span className="cal-legend__chip">!</span>
                  Fuera de {SERVICE_WINDOW_START}–{SERVICE_WINDOW_END}.
                </span>
              </div>

              {data.visits.length === 0 && (
                <p className="muted small mt-2">
                  No hay visitas programadas esta semana. Hacé click en un espacio de la cuadrícula
                  para programar una.
                </p>
              )}
            </div>
          );
        }}
      </Async>

      {creating && (
        <NewVisitModal
          defaultDate={creating.date}
          defaultTime={creating.time}
          onClose={() => setCreating(null)}
        />
      )}
      {viewing && <VisitDetailModal visit={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function VisitChip({ visit, outside, onClick }: {
  visit: Visit; outside?: boolean; onClick: (e: React.MouseEvent) => void;
}) {
  /* La capa va como etiqueta y no como color: el fondo del chip ya significa
     el estado, y pintar también la capa haría que dos cosas distintas
     compitieran por el mismo canal. Solo se marca la extra — la base es el
     caso normal y marcarla sería ruido en todos los chips. */
  const isExtra = visit.visit_kind === 'EXTRA';
  const cls = ['cal-visit', `cal-visit--${visit.status.toLowerCase()}`,
    outside ? 'cal-visit--outside' : ''].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      title={outside
        ? `A las ${visit.scheduled_start}, fuera del horario de servicio (${SERVICE_WINDOW_START}–${SERVICE_WINDOW_END})`
        : VISIT_KIND_HINT[visit.visit_kind]}
    >
      <span className="cal-visit__time">
        {visit.scheduled_start ?? '—'}{outside && ' ⚠'}
      </span>
      <span className="cal-visit__unit">{visit.unit_code}</span>
      <span className="cal-visit__staff">{visit.assigned_to ?? 'Sin asignar'}</span>
      {isExtra && <span className="cal-visit__tag">Extra</span>}
    </button>
  );
}

function VisitDetailModal({ visit, onClose }: { visit: Visit; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [skipping, setSkipping] = useState(false);

  const setStatus = useApiMutation(
    (payload: { status: VisitStatus; reason?: string }) =>
      api.post(`/admin/visits/${visit.id}/status`, { status: payload.status, skip_reason: payload.reason }),
    ['/admin/visits'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={`Consultorio ${visit.unit_code}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cerrar</Button>
          {visit.status === 'SCHEDULED' && !skipping && (
            <>
              <Button onClick={() => setSkipping(true)}>No se realizó</Button>
              <Button
                variant="primary"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ status: 'DONE' })}
              >
                Marcar hecha
              </Button>
            </>
          )}
          {skipping && (
            <Button
              variant="primary"
              disabled={!reason.trim() || setStatus.isPending}
              onClick={() => setStatus.mutate({ status: 'SKIPPED', reason: reason.trim() })}
            >
              Confirmar
            </Button>
          )}
        </>
      }
    >
      {setStatus.error && <Note tone="danger">{setStatus.error.message}</Note>}

      <dl className="dl">
        <dt>Consultorio</dt><dd>{visit.unit_code}{visit.unit_name ? ` · ${visit.unit_name}` : ''}</dd>
        <dt>Fecha</dt><dd>{dateLabel(visit.scheduled_date)}</dd>
        <dt>Capa</dt>
        <dd>{VISIT_KIND_LABEL[visit.visit_kind]} · <span className="muted">{VISIT_KIND_HINT[visit.visit_kind]}</span></dd>
        <dt>Hora</dt>
        <dd>
          {visit.scheduled_start ?? 'Sin hora'}{visit.scheduled_end ? `–${visit.scheduled_end}` : ''}
          {visit.scheduled_start && !isWithinServiceWindow(visit.scheduled_start) && (
            <> · <span style={{ color: 'var(--warn)' }}>
              fuera del horario de servicio ({SERVICE_WINDOW_START}–{SERVICE_WINDOW_END})
            </span></>
          )}
        </dd>
        <dt>Personal</dt><dd>{visit.assigned_to ?? 'Sin asignar'}</dd>
        <dt>Estado</dt>
        <dd><StatusBadge value={visit.status} labels={VISIT_STATUS_LABEL} tones={VISIT_STATUS_TONE} /></dd>
        {visit.skip_reason && <><dt>Motivo</dt><dd>{visit.skip_reason}</dd></>}
      </dl>

      {skipping && (
        <Field label="Motivo por el que no se realizó" required>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
      )}
    </Modal>
  );
}

function NewVisitModal({ defaultDate, defaultTime, onClose }: {
  defaultDate: string; defaultTime?: string; onClose: () => void;
}) {
  const units = useApi<{ units: { id: string; unit_code: string; name: string | null }[] }>('/admin/units');
  const employees = useApi<{ employees: { id: string; full_name: string | null }[] }>('/admin/employees');

  const [form, setForm] = useState({
    service_unit_id: '', scheduled_date: defaultDate, scheduled_start: defaultTime ?? '', scheduled_end: '',
  });
  const [kind, setKind] = useState<VisitKind>('BASE');
  const [assigned, setAssigned] = useState<string[]>([]);

  /* El horario que declaró el consultorio. Se consulta recién cuando hay uno
     elegido: sin consultorio no hay nada que preguntar. Sirve para avisar del
     default de «antes de las 10:00» cuando nadie declaró nada — que es el caso
     de todos los consultorios hoy, porque la pantalla para declararlo todavía
     no existe (CB-06b). */
  const availability = useApi<{ rules: { start_time: string; end_time: string }[] }>(
    form.service_unit_id ? `/admin/units/${form.service_unit_id}/availability` : null);
  const declaredWindow = availability.data && availability.data.rules.length > 0;

  const outsideWindow = Boolean(form.scheduled_start) && !isWithinServiceWindow(form.scheduled_start);
  const extraNeedsTime = kind === 'EXTRA' && !form.scheduled_start;

  const mutation = useApiMutation(
    () => api.post('/admin/visits', {
      service_unit_id: form.service_unit_id,
      scheduled_date: form.scheduled_date,
      scheduled_start: form.scheduled_start || undefined,
      scheduled_end: form.scheduled_end || undefined,
      visit_kind: kind,
      employee_ids: assigned,
    }),
    ['/admin/visits'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title="Programar visita"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary"
            disabled={!form.service_unit_id || extraNeedsTime || mutation.isPending}
            onClick={() => mutation.mutate()}>Programar</Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Consultorio" required>
        <select className="select" value={form.service_unit_id}
          onChange={(e) => setForm((f) => ({ ...f, service_unit_id: e.target.value }))}>
          <option value="">Elegir…</option>
          {(units.data?.units ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.unit_code}{u.name ? ` · ${u.name}` : ''}</option>
          ))}
        </select>
      </Field>

      {/* Sin horario declarado el consultorio no queda fuera de la
          programación: entra al default de limpiar antes de las 10:00
          (decisión del dueño). Se dice acá porque es el momento en que
          importa, y porque hoy ningún consultorio tiene horario cargado —la
          pantalla para declararlo llega con CB-06. */}
      {form.service_unit_id && availability.data && !declaredWindow && (
        <Note tone="info">
          Este consultorio no declaró su horario. Por defecto se limpia antes de
          las {DEFAULT_CLEANING_DEADLINE}.
        </Note>
      )}

      <Field label="Capa" hint={VISIT_KIND_HINT[kind]}>
        <select className="select" value={kind}
          onChange={(e) => setKind(e.target.value as VisitKind)}>
          {Object.entries(VISIT_KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>

      <div className="grid grid--2">
        <Field label="Fecha" required>
          <input className="input" type="date" value={form.scheduled_date}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} />
        </Field>
        <Field
          label="Hora"
          required={kind === 'EXTRA'}
          error={extraNeedsTime
            ? 'Una visita extra necesita hora: entra en la capacidad que queda libre ese día.'
            : undefined}
          hint={outsideWindow
            ? `Fuera del horario de servicio (${SERVICE_WINDOW_START}–${SERVICE_WINDOW_END}). Se va a programar igual, y el calendario la muestra aparte.`
            : undefined}
        >
          <input className="input" type="time" value={form.scheduled_start}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_start: e.target.value }))} />
        </Field>
      </div>

      <Field label="Personal asignado">
        <div style={{ display: 'grid', gap: 6 }}>
          {(employees.data?.employees ?? []).map((e) => (
            <label key={e.id} className="row small">
              <input
                type="checkbox"
                checked={assigned.includes(e.id)}
                onChange={(ev) => setAssigned((prev) =>
                  ev.target.checked ? [...prev, e.id] : prev.filter((id) => id !== e.id))}
              />
              {e.full_name ?? '—'}
            </label>
          ))}
        </div>
      </Field>
    </Modal>
  );
}
